import React from 'react';
import { Icon } from '@/src/components/ui';
import { TooltipComponent } from '@/src/components/layout/TooltipComponent';
import { ratingWording } from '@/src/utils/helpers';
import { RatingsBreakdown, StarValue } from 'types/src/types';

const STARS: readonly { rating: StarValue; label: string }[] = [
  { rating: 5, label: 'Excellent' },
  { rating: 4, label: 'Good' },
  { rating: 3, label: 'Mixed' },
  { rating: 2, label: 'Poor' },
  { rating: 1, label: 'Bad' },
];
const sum = (arr: number[]) => arr.reduce((a, b) => a + b, 0);

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

const Star = ({ rating }: { rating: number }) => {
  const stars: React.ReactNode[] = [];

  for (let i = 1; i <= 5; i++) {
    const fillPct = clamp01(rating - (i - 1)) * 100;

    stars.push(
      <div key={i} className="relative inline-block leading-none">
        <Icon name="star" className="size-5 text-gray-300" />
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ width: `${fillPct}%` }}
        >
          <Icon name="star" className="size-5 text-blue-primary" />
        </div>
      </div>
    );
  }

  return <div className="flex gap-1">{stars}</div>;
};

const RatingLine = ({ pct }: { pct: number }) => {
  return (
    <div className="flex items-center gap-3 w-full">
      <div className="flex-1 h-2 bg-black/5 rounded-full overflow-hidden">
        <div
          className="h-full bg-blue-primary rounded-full transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

export const RatingBar = ({
  averageRating,
  totalReviews,
  breakdowns = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
}: {
  averageRating: number;
  totalReviews: number;
  breakdowns: RatingsBreakdown;
}) => {
  const safeRatings: Record<StarValue, number> = {
    5: breakdowns[5] ?? 0,
    4: breakdowns[4] ?? 0,
    3: breakdowns[3] ?? 0,
    2: breakdowns[2] ?? 0,
    1: breakdowns[1] ?? 0,
  };

  const totalFromRatings = sum([
    safeRatings[1],
    safeRatings[2],
    safeRatings[3],
    safeRatings[4],
    safeRatings[5],
  ]);

  const computedTotal =
    totalFromRatings > 0 ? totalFromRatings : (totalReviews ?? 0);

  return (
    <div className="flex gap-12 md:py-8 max-w-4xl">
      <div className="flex flex-col items-start gap-4 min-w-[180px]">
        <div className="flex gap-2 items-end">
          <span className="md:text-[40px] text-2xl font-semibold text-black/80 leading-none">
            {averageRating.toFixed(1)}
          </span>
          <span className="text-sm text-black/80 font-semibold">
            {ratingWording(averageRating)}
          </span>
        </div>
        <Star rating={averageRating} />
      </div>

      <div className="flex-1 flex flex-col gap-2">
        {STARS.map(({ rating, label }) => {
          const pct =
            computedTotal > 0 ? (safeRatings[rating] / computedTotal) * 100 : 0;
          const percent = `${Math.round(pct)}%`;
          return (
            <div key={rating} className="flex items-center gap-3">
              <TooltipComponent
                className="max-w-none"
                side="top"
                text={` ${safeRatings[rating].toLocaleString()} reviews (${percent}) - are ${label}`}
              >
                <RatingLine pct={pct} />
              </TooltipComponent>
            </div>
          );
        })}
      </div>
    </div>
  );
};
